import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Row, Col, message, Divider, Space, Typography, Cascader, Table, Tag, Button } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, TeamOutlined } from '@ant-design/icons';
import { createLead, checkPhone, claimLead } from '../../services/lead';
import { getAssignableUsers, UserAssignee } from '../../services/user';
import { validateLeadData, sanitizeApiData } from '../../utils/validation';
import { DATA_SOURCE_OPTIONS, URGENCY_LEVEL_OPTIONS, LEAD_TYPE_OPTIONS } from '../../constants/leadConstants';
import { REGION_DATA } from '../../utils/regionData';
import { getCurrentUser } from '../../services/auth';
import { UserRole } from '../../constants/user';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

interface CreateLeadModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const STORAGE_KEY = 'create_lead_form_data';

const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserAssignee[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [phoneCheckResult, setPhoneCheckResult] = useState<any>(null);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  // 加载缓存的表单数据
  useEffect(() => {
    if (visible) {
      loadCurrentUser();
      loadUsers();
      
      // 尝试从 localStorage 加载缓存数据
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // 检查是否有实际内容
          const hasContent = Object.values(parsed).some(value => {
            if (typeof value === 'string') {
              return value.trim().length > 0;
            }
            return value != null && value !== '';
          });
          
          if (hasContent) {
            form.setFieldsValue(parsed);
            setFormChanged(true);
          } else {
            form.resetFields();
            setFormChanged(false);
          }
        } catch (error) {
          console.error('Failed to parse saved lead data:', error);
          form.resetFields();
          setFormChanged(false);
        }
      } else {
        form.resetFields();
        setFormChanged(false);
      }
      
      // 重置检查状态
      setPhoneCheckResult(null);
      setShowPhoneWarning(false);
    }
  }, [visible]);

  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      setIsAdmin(user.role === UserRole.ADMIN);
      
      // 默认分配给自己
      form.setFieldsValue({ assigned_to: user.id });
    } catch (error) {
      console.error('Failed to load current user', error);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getAssignableUsers({ limit: 100 });
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to load users', error);
    }
  };

  // 检查手机号
  const handlePhoneCheck = async (phone: string) => {
    if (!phone || phone.length < 11) {
      setPhoneCheckResult(null);
      setShowPhoneWarning(false);
      return;
    }

    try {
      const result = await checkPhone(phone);
      setPhoneCheckResult(result);
      
      // 如果在黑名单中，立即弹窗提示
      if (result.is_blacklisted) {
        Modal.error({
          title: '该号码已在黑名单中',
          content: (
            <div>
              <p>拉黑原因：{result.blacklist_remark || '无'}</p>
              <p style={{ color: '#ff4d4f', marginTop: 8 }}>无法为黑名单号码创建线索</p>
            </div>
          ),
        });
        setShowPhoneWarning(true);
      } 
      // 如果已有关联线索，弹窗展示
      else if (result.has_existing_leads) {
        Modal.error({
          title: `手机号已关联 ${result.existing_leads.length} 条线索，不允许重复创建`,
          width: 900,
          content: (
            <div>
              <p style={{ marginBottom: 12, color: '#ff4d4f' }}>
                该手机号已存在以下线索，请直接认领或联系负责人：
              </p>
              <Table
                size="small"
                dataSource={result.existing_leads}
                rowKey="id"
                pagination={false}
                scroll={{ x: 800 }}
                columns={[
                  { 
                    title: '联系人', 
                    dataIndex: 'contact_name', 
                    width: 80,
                    fixed: 'left'
                  },
                  { 
                    title: '跟进意向', 
                    dataIndex: 'follow_status',
                    width: 90,
                    render: (status: string) => status || '未联系'
                  },
                  { 
                    title: '负责人', 
                    dataIndex: 'assignee_name',
                    width: 90,
                    render: (name: string, record: any) => {
                      // 根据status判断是否已分配
                      if (record.status === 'ASSIGNED') {
                        return name || '-';
                      }
                      return '-';
                    }
                  },
                  { 
                    title: '案件描述', 
                    dataIndex: 'case_description',
                    width: 150,
                    ellipsis: {
                      showTitle: false,
                    },
                    render: (text: string) => (
                      <span title={text || '-'}>{text || '-'}</span>
                    )
                  },
                  { 
                    title: '最新跟进', 
                    dataIndex: 'latest_follow_up',
                    width: 150,
                    ellipsis: {
                      showTitle: false,
                    },
                    render: (text: string) => (
                      <span title={text || '-'}>{text || '-'}</span>
                    )
                  },
                  { 
                    title: '紧急程度', 
                    dataIndex: 'urgency_level',
                    width: 80,
                    render: (level: string) => level || '-'
                  },
                  { 
                    title: '上次跟进', 
                    dataIndex: 'last_follow_time',
                    width: 100,
                    render: (time: string) => time ? new Date(time).toLocaleDateString('zh-CN') : '-'
                  },
                  { 
                    title: '创建时间', 
                    dataIndex: 'created_at',
                    width: 100,
                    render: (time: string) => new Date(time).toLocaleDateString('zh-CN')
                  },
                  {
                    title: '操作',
                    width: 80,
                    fixed: 'right',
                    render: (_: any, record: any) => {
                      // 根据status判断是否可以认领
                      if (record.status === 'PUBLIC_POOL') {
                        return (
                          <a onClick={() => {
                            Modal.destroyAll();
                            handleClaimExistingLead(record.id);
                          }}>认领</a>
                        );
                      }
                      return <span style={{ color: '#999' }}>已分配</span>;
                    }
                  }
                ]}
              />
            </div>
          ),
        });
        setShowPhoneWarning(true);
      } else {
        setShowPhoneWarning(false);
      }
    } catch (error) {
      console.error('检查手机号失败', error);
    }
  };

  // 认领已存在的线索
  const handleClaimExistingLead = async (leadId: number) => {
    try {
      await claimLead(leadId);
      message.success('认领成功');
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.detail || '认领失败');
    }
  };

  const handleSubmit = async (values: any) => {
    // 如果手机号在黑名单中，阻止创建
    if (phoneCheckResult?.is_blacklisted) {
      Modal.warning({
        title: '该号码已在黑名单中',
        content: `拉黑原因：${phoneCheckResult.blacklist_remark || '无'}`,
      });
      return;
    }

    // 如果已有关联线索，阻止创建
    if (phoneCheckResult?.has_existing_leads) {
      message.error(`手机号已关联 ${phoneCheckResult.existing_leads.length} 条线索，不允许重复创建`);
      return;
    }

    await proceedCreate(values);
  };

  const proceedCreate = async (values: any) => {
    setLoading(true);
    try {
      // Validate data
      const validation = validateLeadData(values);
      if (!validation.isValid) {
        validation.errors.forEach(error => message.error(error));
        setLoading(false);
        return;
      }

      // Handle region data
      if (values.region && Array.isArray(values.region)) {
        values.province = values.region[0];
        values.city = values.region[1];
        values.district = values.region[2];
        delete values.region;
      }

      // Sanitize data
      const sanitizedData = sanitizeApiData(values);

      await createLead(sanitizedData);
      message.success('创建线索成功');
      
      // 清除缓存数据
      localStorage.removeItem(STORAGE_KEY);
      
      form.resetFields();
      setPhoneCheckResult(null);
      setShowPhoneWarning(false);
      setFormChanged(false);
      onSuccess();
      onClose();
    } catch (error) {
      message.error('创建线索失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理表单值变化，保存到 localStorage（但不改变 formChanged 状态）
  const handleFormChange = () => {
    const values = form.getFieldsValue();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  };

  // 检查是否有未提交的内容
  const hasUnsavedContent = () => {
    const values = form.getFieldsValue();
    return Object.values(values).some(value => {
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return value != null && value !== '';
    });
  };

  // 处理关闭弹窗
  const handleClose = () => {
    if (hasUnsavedContent()) {
      Modal.confirm({
        title: '确认关闭',
        content: '关闭后数据将保存在本地，下次打开时可以继续填写',
        okText: '确认',
        cancelText: '取消',
        onOk: () => {
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  // 清除缓存数据
  const handleClearCache = () => {
    localStorage.removeItem(STORAGE_KEY);
    form.resetFields();
    setFormChanged(false);
    message.success('已清除缓存数据');
  };

  return (
    <Modal
      title={
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <span style={{ fontSize: '16px', fontWeight: 600 }}>新建线索</span>
          {formChanged && (
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
              (已缓存)
            </Text>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={680}
      okText="创建线索"
      cancelText="取消"
      destroyOnClose={false}
      bodyStyle={{ paddingTop: 24 }}
      okButtonProps={{
        disabled: showPhoneWarning
      }}
    >
      {formChanged && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#e6f7ff', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            检测到未提交的数据，已自动恢复
          </Text>
          <Button size="small" type="link" onClick={handleClearCache}>
            清除缓存
          </Button>
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
        autoComplete="off"
        onValuesChange={handleFormChange}
      >
        {/* 基本信息 */}
        <Divider orientation="left" style={{ marginTop: 0 }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>基本信息</Text>
        </Divider>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contact_name"
              label="联系人姓名"
              rules={[{ required: true, message: '请输入联系人姓名' }]}
            >
              <Input 
                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="请输入姓名" 
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="手机号码"
              rules={[
                { required: true, message: '请输入手机号' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }
              ]}
              validateStatus={showPhoneWarning ? 'error' : undefined}
              help={
                phoneCheckResult?.is_blacklisted ? (
                  <span style={{ color: '#ff4d4f' }}>
                    ⚠️ 该号码已在黑名单中，无法创建线索
                  </span>
                ) : phoneCheckResult?.has_existing_leads ? (
                  <span style={{ color: '#ff4d4f' }}>
                    ⚠️ 该手机号已关联 {phoneCheckResult.existing_leads.length} 条线索，不允许重复创建
                  </span>
                ) : undefined
              }
            >
              <Input 
                prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="请输入手机号" 
                size="large"
                onChange={(e) => handlePhoneCheck(e.target.value)}
                status={showPhoneWarning ? 'error' : undefined}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="email" 
              label="电子邮箱"
              rules={[
                { type: 'email', message: '请输入正确的邮箱地址' }
              ]}
            >
              <Input 
                prefix={<MailOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="example@email.com" 
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="region" label="所在地区">
              <Cascader
                options={REGION_DATA}
                placeholder="请选择省/市/区"
                size="large"
                // 禁用搜索功能以避免浏览器自动填充
                // showSearch
                changeOnSelect
                popupClassName="region-cascader-popup"
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="address" label="详细地址">
              <Input 
                prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="请输入详细地址（街道、门牌号等）" 
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 线索信息 */}
        <Divider orientation="left">
          <Text type="secondary" style={{ fontSize: '13px' }}>线索信息</Text>
        </Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="data_source" label="数据来源">
              <Select 
                placeholder="请选择数据来源" 
                size="large"
                allowClear
              >
                {DATA_SOURCE_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="lead_type" label="线索类型">
              <Select 
                placeholder="请选择线索类型" 
                size="large"
                allowClear
              >
                {LEAD_TYPE_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="follow_status" 
              label="跟进意向"
              initialValue="未联系"
            >
              <Select 
                placeholder="请选择跟进意向" 
                size="large"
              >
                <Option value="未联系">📋 未联系</Option>
                <Option value="未接通">📞 未接通</Option>
                <Option value="不需要">🚫 不需要</Option>
                <Option value="无人接听">📵 无人接听</Option>
                <Option value="无效">❌ 无效</Option>
                <Option value="空号">📵 空号</Option>
                <Option value="停机">🔇 停机</Option>
                <Option value="稍后联系">⏰ 稍后联系</Option>
                <Option value="重复">🔄 重复</Option>
                <Option value="客诉">⚠️ 客诉</Option>
                <Option value="已加微信">💬 已加微信</Option>
                <Option value="已加企微">👥 已加企微</Option>
                <Option value="已付咨询费">💰 已付咨询费</Option>
                <Option value="邀约到所">🏢 邀约到所</Option>
                <Option value="已成交">✅ 已成交</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="urgency_level" 
              label="紧急程度"
              initialValue="D类"
            >
              <Select 
                placeholder="请选择紧急程度" 
                size="large"
              >
                {URGENCY_LEVEL_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>{option.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item 
              name="assigned_to" 
              label="分配负责人"
              tooltip={
                isAdmin 
                  ? "管理员可选择分配给任何员工，留空则放入公海池" 
                  : "可选择分配给自己或留空放入公海池"
              }
            >
              <Select
                showSearch
                placeholder="选择负责人（留空则放入公海池）"
                optionFilterProp="children"
                allowClear
                size="large"
                suffixIcon={<TeamOutlined />}
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {users
                  .filter(user => isAdmin || user.id === currentUser?.id)
                  .map(user => (
                    <Option 
                      key={user.id} 
                      value={user.id} 
                      label={user.real_name || `用户${user.id}`}
                    >
                      <Space>
                        <UserOutlined />
                        <span>{user.real_name || `用户${user.id}`}</span>
                        {user.department && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {user.department}
                          </Text>
                        )}
                      </Space>
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>



        {/* 详细描述 */}
        <Divider orientation="left">
          <Text type="secondary" style={{ fontSize: '13px' }}>详细描述</Text>
        </Divider>

        <Form.Item name="case_description" label="案件描述">
          <TextArea 
            rows={3} 
            placeholder="请简要描述案件情况、客户需求等信息"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item name="description" label="备注信息">
          <TextArea 
            rows={2} 
            placeholder="其他需要记录的信息"
            showCount
            maxLength={200}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateLeadModal;
